import { useAuth } from '../../hooks/useAuth';
import { useDayStore } from '../../stores/dayStore';
import { NotificationDropdown } from './NotificationDropdown';
import { ThemeToggle } from '../shared/ThemeToggle';
import { useLocation } from 'react-router-dom';

const ROUTE_TITLES: Record<string, string> = {
  '/dashboard':    'Overview',
  '/stock':        'Stock',
  '/sales':        'Sales',
  '/customers':    'Vendors',
  '/credits':      'Credits',
  '/receivables':  'Receivables',
  '/expenses':     'Expenses',
  '/eod':          'End of Day',
  '/invoices':     'Invoices',
  '/cash-in-bank': 'Cash in Bank',
  '/settings':     'Settings',
};

function formatDate(dateStr: string | null): string {
  const d = dateStr ? new Date(dateStr) : new Date();
  return d.toLocaleDateString('en-IN', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

interface TopBarProps {
  onToggleSidebar: () => void;
  sidebarOpen: boolean;
  /** True only on desktop when sidebar is fully visible (persistent mode) */
  sidebarPersistent: boolean;
}

export function TopBar({ onToggleSidebar, sidebarOpen, sidebarPersistent }: TopBarProps) {
  const { user, logout } = useAuth();
  const { currentDate, dayStatus } = useDayStore();
  const location = useLocation();

  const pageTitle = ROUTE_TITLES[location.pathname] ?? 'Overview';

  const initials = user?.name
    ? user.name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
    : '??';

  return (
    <header
      className="fixed top-0 right-0 z-20 bg-saas-card border-b border-saas-border flex items-center px-4 sm:px-5 gap-3 transition-all duration-300"
      style={{
        left: sidebarPersistent ? 'var(--sidebar-width)' : 0,
        height: 'var(--topbar-height)',
        borderTopLeftRadius: sidebarPersistent ? '1rem' : 0,
      }}
    >
      {/* Hamburger / toggle button */}
      <button
        onClick={onToggleSidebar}
        className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-lg text-saas-muted hover:text-saas-text hover:bg-saas-bg transition-all duration-150"
        aria-label={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      {/* Page title + status */}
      <div className="flex-1 min-w-0">
        <h1 className="text-base sm:text-lg font-bold text-saas-text tracking-tight truncate leading-tight">
          {pageTitle}
        </h1>
        <div className="flex items-center gap-2">
          <span className="text-xs text-saas-muted hidden sm:block truncate">
            {formatDate(currentDate)}
          </span>
          <span className="hidden sm:block text-saas-border">·</span>
          <span
            className={[
              'inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full',
              dayStatus === 'OPEN'
                ? 'bg-emerald-50 text-emerald-700'
                : dayStatus === 'CLOSED'
                ? 'bg-slate-100 text-saas-muted'
                : 'bg-amber-50 text-amber-700',
            ].join(' ')}
          >
            <span
              className={[
                'w-1.5 h-1.5 rounded-full',
                dayStatus === 'OPEN' ? 'bg-emerald-500' : dayStatus === 'CLOSED' ? 'bg-slate-400' : 'bg-amber-500',
              ].join(' ')}
            />
            {dayStatus === 'UNKNOWN' ? 'Loading…' : `Day ${dayStatus}`}
          </span>
        </div>
      </div>

      {/* Right actions */}
      <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
        <ThemeToggle />

        <NotificationDropdown />

        <div className="w-px h-6 bg-saas-border mx-1 hidden sm:block" />

        {/* User name — hidden on mobile */}
        <div className="hidden sm:block text-right">
          <p className="text-sm font-semibold text-saas-text truncate max-w-[130px] leading-tight">
            {user?.name ?? 'Unknown'}
          </p>
          <p className="text-xs text-saas-muted truncate max-w-[130px]">
            {user?.roles.join(', ') ?? ''}
          </p>
        </div>

        {/* Avatar */}
        <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-brand-600 text-white flex items-center justify-center text-sm font-bold flex-shrink-0 ring-2 ring-brand-100 overflow-hidden">
          {user?.avatarUrl ? (
            <img src={user.avatarUrl} alt={user.name} className="w-full h-full object-cover" />
          ) : (
            <span className="text-xs sm:text-sm">{initials}</span>
          )}
        </div>

        {/* Logout */}
        <button
          onClick={() => void logout()}
          className="p-2 text-saas-muted hover:text-saas-error hover:bg-red-50 rounded-lg transition-all duration-150"
          title="Logout"
          aria-label="Logout"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
        </button>
      </div>
    </header>
  );
}
